import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';

/**
 * WizardContext - Manages setup wizard state across all 4 steps
 *
 * Features:
 * - Step-by-step navigation (1-4)
 * - Data collection across all wizard steps
 * - Validation state management
 * - Draft persistence (survive page refresh)
 * - Wizard completion tracking
 *
 * State Shape:
 * {
 *   currentStep: 1-4,
 *   gardenData: {
 *     basics: { name, length, width, soilType, phValue, drainage, orientation, sunlight, zipCode, city },
 *     backgroundImage: { file, dataUrl, scale, transparency, position, rotation, locked },
 *     outline: { vertices, isClosed },
 *     heightDifferences: { enabled, zones, stairs, slopes, baseSlope }
 *   },
 *   validationErrors: {},
 *   isComplete: boolean
 * }
 */

const WizardContext = createContext(null);

// localStorage key for wizard draft persistence
const DRAFT_STORAGE_KEY = 'gartenplaner_wizard_draft';

// Initial state structure
const initialState = {
  currentStep: 1,
  gardenData: {
    basics: {
      name: 'Mein Garten', // Default garden name
      soilType: null,
      phValue: 7.0, // Default neutral pH
      drainage: null,
      orientation: null,
      sunlight: null,
      zipCode: null,
      city: null,
      country: null,
      hardinessZone: null,
      // Geo-Koordinaten aus dem PLZ-Lookup (#604) — Basis für den Sonnenstand
      // in der 3D-Ansicht. Additiv, kein DB-Schema-Change (Muster wie layers).
      latitude: null,
      longitude: null,
    },
    backgroundImage: {
      file: null,
      dataUrl: null,
      calibration: {
        point1: null,           // {x, y} canvas pixels
        point2: null,           // {x, y} canvas pixels
        pixelDistance: null,    // calculated distance
        realDistance: 5,        // meters (user input)
        scale: null,            // pixelDistance / realDistance
        isCalibrated: false,    // true when both points set
      },
      transparency: 50, // 0-100%
      position: { x: 0, y: 0 },
      rotation: 0, // -180 to 180 degrees
      locked: false,
    },
    outline: {
      vertices: [], // [{x, y}, ...]
      isClosed: false,
      skipped: false, // mobile: user skipped polygon drawing
      rect: { width: 10, depth: 10 }, // mobile: simple rectangle dimensions (meters)
    },
    heightDifferences: {
      enabled: false,
      zones: [], // [{id, vertices, height, color}, ...]
      stairs: [], // [{id, start, end, steps}, ...]
      slopes: [], // [{id, vertices, startHeight, endHeight}, ...]
      maxHeight: 0, // legacy mobile slider (meters) — superseded by baseSlope (#624)
      // Grundgefälle (#624): null = eben. directionDeg = Canvas-Winkel (0–359,
      // Richtung, in die das Gelände ABFÄLLT), dropCm = Gesamtgefälle 10–500 cm.
      baseSlope: null,
    },
  },
  validationErrors: {},
  isComplete: false,
};

// Action types
const ACTIONS = {
  GO_TO_STEP: 'GO_TO_STEP',
  NEXT_STEP: 'NEXT_STEP',
  PREVIOUS_STEP: 'PREVIOUS_STEP',
  UPDATE_BASICS: 'UPDATE_BASICS',
  UPDATE_BACKGROUND_IMAGE: 'UPDATE_BACKGROUND_IMAGE',
  UPDATE_OUTLINE: 'UPDATE_OUTLINE',
  UPDATE_HEIGHT_DIFFERENCES: 'UPDATE_HEIGHT_DIFFERENCES',
  SET_VALIDATION_ERRORS: 'SET_VALIDATION_ERRORS',
  CLEAR_VALIDATION_ERRORS: 'CLEAR_VALIDATION_ERRORS',
  COMPLETE_WIZARD: 'COMPLETE_WIZARD',
  RESET_WIZARD: 'RESET_WIZARD',
  LOAD_DRAFT: 'LOAD_DRAFT',
  LOAD_GARDEN_DATA: 'LOAD_GARDEN_DATA',
};

/**
 * Wizard reducer - handles all state transitions
 */
function wizardReducer(state, action) {
  switch (action.type) {
    case ACTIONS.GO_TO_STEP:
      return {
        ...state,
        currentStep: Math.max(1, Math.min(4, action.payload)),
      };

    case ACTIONS.NEXT_STEP:
      return {
        ...state,
        currentStep: Math.min(4, state.currentStep + 1),
      };

    case ACTIONS.PREVIOUS_STEP:
      return {
        ...state,
        currentStep: Math.max(1, state.currentStep - 1),
      };

    case ACTIONS.UPDATE_BASICS:
      return {
        ...state,
        gardenData: {
          ...state.gardenData,
          basics: {
            ...state.gardenData.basics,
            ...action.payload,
          },
        },
      };

    case ACTIONS.UPDATE_BACKGROUND_IMAGE:
      return {
        ...state,
        gardenData: {
          ...state.gardenData,
          backgroundImage: {
            ...state.gardenData.backgroundImage,
            ...action.payload,
          },
        },
      };

    case ACTIONS.UPDATE_OUTLINE:
      return {
        ...state,
        gardenData: {
          ...state.gardenData,
          outline: {
            ...state.gardenData.outline,
            ...action.payload,
          },
        },
      };

    case ACTIONS.UPDATE_HEIGHT_DIFFERENCES:
      return {
        ...state,
        gardenData: {
          ...state.gardenData,
          heightDifferences: {
            ...state.gardenData.heightDifferences,
            ...action.payload,
          },
        },
      };

    case ACTIONS.SET_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: {
          ...state.validationErrors,
          ...action.payload,
        },
      };

    case ACTIONS.CLEAR_VALIDATION_ERRORS:
      return {
        ...state,
        validationErrors: {},
      };

    case ACTIONS.COMPLETE_WIZARD:
      return {
        ...state,
        isComplete: true,
      };

    case ACTIONS.RESET_WIZARD:
      return {
        ...initialState,
        currentStep: 1,
        isComplete: false,
      };

    case ACTIONS.LOAD_DRAFT:
      return {
        ...state,
        ...action.payload,
      };

    case ACTIONS.LOAD_GARDEN_DATA:
      return {
        ...state,
        gardenData: {
          ...initialState.gardenData,
          ...action.payload,
          basics: {
            ...initialState.gardenData.basics,
            ...(action.payload.basics || {}),
          },
          backgroundImage: {
            ...initialState.gardenData.backgroundImage,
            ...(action.payload.backgroundImage || {}),
          },
          outline: {
            ...initialState.gardenData.outline,
            ...(action.payload.outline || {}),
          },
          heightDifferences: {
            ...initialState.gardenData.heightDifferences,
            ...(action.payload.heightDifferences || {}),
          },
        },
      };

    default:
      return state;
  }
}

/**
 * Load wizard draft from localStorage
 */
function loadDraftFromStorage() {
  try {
    const saved = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge with initial state to handle schema changes
      return {
        ...initialState,
        ...parsed,
        gardenData: {
          ...initialState.gardenData,
          ...parsed.gardenData,
          basics: {
            ...initialState.gardenData.basics,
            ...parsed.gardenData?.basics,
          },
          backgroundImage: {
            ...initialState.gardenData.backgroundImage,
            ...parsed.gardenData?.backgroundImage,
            calibration: {
              ...initialState.gardenData.backgroundImage.calibration,
              ...parsed.gardenData?.backgroundImage?.calibration,
            },
          },
          outline: {
            ...initialState.gardenData.outline,
            ...parsed.gardenData?.outline,
            rect: {
              ...initialState.gardenData.outline.rect,
              ...parsed.gardenData?.outline?.rect,
            },
          },
          heightDifferences: {
            ...initialState.gardenData.heightDifferences,
            ...parsed.gardenData?.heightDifferences,
          },
        },
      };
    }
  } catch (error) {
    console.error('Failed to load wizard draft from localStorage:', error);
  }
  return initialState;
}

/**
 * Save wizard draft to localStorage
 */
function saveDraftToStorage(state) {
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save wizard draft to localStorage:', error);
  }
}

/**
 * Clear wizard draft from localStorage
 */
function clearDraftFromStorage() {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear wizard draft from localStorage:', error);
  }
}

/**
 * Validate Step 1 - Garden Basics
 */
function validateStep1(basics) {
  const errors = {};

  // Soil type validation (required)
  if (!basics.soilType) {
    errors.soilType = 'Bodenart ist erforderlich';
  }

  // pH value validation (required, must be in range)
  if (basics.phValue === null || basics.phValue === undefined) {
    errors.phValue = 'pH-Wert ist erforderlich';
  } else if (basics.phValue < 4.0 || basics.phValue > 8.0) {
    errors.phValue = 'pH-Wert muss zwischen 4.0 und 8.0 liegen';
  }

  // Drainage validation (required)
  if (!basics.drainage) {
    errors.drainage = 'Drainage ist erforderlich';
  }

  // Orientation validation (required)
  if (!basics.orientation) {
    errors.orientation = 'Ausrichtung ist erforderlich';
  }

  // ZIP code validation (required)
  if (!basics.zipCode || basics.zipCode.trim() === '') {
    errors.zipCode = 'Postleitzahl ist erforderlich';
  }

  // City validation (auto-filled from ZIP, but required)
  if (!basics.city) {
    errors.city = 'Stadt ist erforderlich (bitte gültige PLZ eingeben)';
  }

  return errors;
}

/**
 * Validate Step 2 - Background Image
 */
function validateStep2(backgroundImage) {
  const errors = {};

  // If an image is uploaded, scale must be calibrated
  if (backgroundImage.dataUrl && (!backgroundImage.scale || backgroundImage.scale === 1.0)) {
    errors.scale = 'Bitte kalibrieren Sie den Maßstab des Bildes';
  }

  return errors;
}

/**
 * Validate Step 3 - Garden Outline
 */
function validateStep3(outline) {
  const errors = {};

  // Mobile: skipped outline is valid (user can draw later on desktop)
  if (outline.skipped) return errors;

  // Outline must have at least 3 vertices to form a polygon
  if (!outline.vertices || outline.vertices.length < 3) {
    errors.outline = 'Zeichnen Sie mindestens 3 Punkte für die Gartengrenze';
    return errors;
  }

  // Outline should be closed
  if (!outline.isClosed) {
    errors.outline = 'Schließen Sie die Gartengrenze ab';
  }

  return errors;
}

/**
 * Validate Step 4 - Height Differences (optional step)
 */
function validateStep4(_heightDifferences) {
  // Step 4 is optional, no required fields
  return {};
}

/**
 * WizardProvider - Context provider for wizard state
 */
export const WizardProvider = ({ children }) => {
  // Initialize state with draft from localStorage
  const [state, dispatch] = useReducer(wizardReducer, null, loadDraftFromStorage);

  // Auto-save draft to localStorage on state change
  useEffect(() => {
    // Don't save if wizard is complete
    if (state.isComplete) {
      clearDraftFromStorage();
      return;
    }

    // Don't save if this is a fresh reset (all required fields are null and step is 1)
    const isFreshReset =
      state.currentStep === 1 &&
      state.gardenData.basics.soilType === null &&
      state.gardenData.basics.drainage === null &&
      state.gardenData.basics.orientation === null &&
      state.gardenData.outline.vertices.length === 0;

    if (!isFreshReset) {
      saveDraftToStorage(state);
    }
  }, [state]);

  // Navigation actions
  const goToStep = useCallback((stepNumber) => {
    dispatch({ type: ACTIONS.GO_TO_STEP, payload: stepNumber });
  }, []);

  const nextStep = useCallback(() => {
    dispatch({ type: ACTIONS.NEXT_STEP });
  }, []);

  const previousStep = useCallback(() => {
    dispatch({ type: ACTIONS.PREVIOUS_STEP });
  }, []);

  // Data update actions
  const updateBasics = useCallback((data) => {
    dispatch({ type: ACTIONS.UPDATE_BASICS, payload: data });
  }, []);

  const updateBackgroundImage = useCallback((data) => {
    dispatch({ type: ACTIONS.UPDATE_BACKGROUND_IMAGE, payload: data });
  }, []);

  const updateOutline = useCallback((data) => {
    dispatch({ type: ACTIONS.UPDATE_OUTLINE, payload: data });
  }, []);

  const updateHeightDifferences = useCallback((data) => {
    dispatch({ type: ACTIONS.UPDATE_HEIGHT_DIFFERENCES, payload: data });
  }, []);

  // Validation actions
  const validateStep = useCallback(
    (stepNumber) => {
      let errors = {};

      switch (stepNumber) {
        case 1:
          errors = validateStep1(state.gardenData.basics);
          break;
        case 2:
          errors = validateStep2(state.gardenData.backgroundImage);
          break;
        case 3:
          errors = validateStep3(state.gardenData.outline);
          break;
        case 4:
          errors = validateStep4(state.gardenData.heightDifferences);
          break;
        default:
          break;
      }

      if (Object.keys(errors).length > 0) {
        dispatch({ type: ACTIONS.SET_VALIDATION_ERRORS, payload: errors });
        return false;
      }

      dispatch({ type: ACTIONS.CLEAR_VALIDATION_ERRORS });
      return true;
    },
    [state.gardenData]
  );

  // Wizard completion
  const completeWizard = useCallback(() => {
    dispatch({ type: ACTIONS.COMPLETE_WIZARD });
  }, []);

  const resetWizard = useCallback(() => {
    dispatch({ type: ACTIONS.RESET_WIZARD });
    clearDraftFromStorage();
  }, []);

  /**
   * Bulk-load garden data from backend into WizardContext.
   * Used by useGardenLoader after switching gardens so that
   * "Einstellungen bearbeiten" shows the correct garden's data.
   */
  const loadGardenData = useCallback((gardenData) => {
    dispatch({ type: ACTIONS.LOAD_GARDEN_DATA, payload: gardenData });
  }, []);

  // Manual draft management
  const loadDraft = useCallback(() => {
    const draft = loadDraftFromStorage();
    dispatch({ type: ACTIONS.LOAD_DRAFT, payload: draft });
  }, []);

  const saveDraft = useCallback(() => {
    saveDraftToStorage(state);
  }, [state]);

  const clearDraft = useCallback(() => {
    clearDraftFromStorage();
  }, []);

  // Context value
  const value = {
    // State
    currentStep: state.currentStep,
    gardenData: state.gardenData,
    validationErrors: state.validationErrors,
    isComplete: state.isComplete,

    // Navigation actions
    goToStep,
    nextStep,
    previousStep,

    // Data update actions
    updateBasics,
    updateBackgroundImage,
    updateOutline,
    updateHeightDifferences,

    // Validation
    validateStep,

    // Wizard lifecycle
    completeWizard,
    resetWizard,
    loadGardenData,

    // Draft management
    loadDraft,
    saveDraft,
    clearDraft,
  };

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

/**
 * useWizard - Hook to access wizard context
 *
 * Usage:
 * const {
 *   currentStep,
 *   gardenData,
 *   validationErrors,
 *   goToStep,
 *   updateBasics,
 *   validateStep,
 *   completeWizard
 * } = useWizard();
 *
 * @throws {Error} If used outside of WizardProvider
 */
export const useWizard = () => {
  const context = useContext(WizardContext);
  if (!context) {
    throw new Error('useWizard must be used within a WizardProvider');
  }
  return context;
};
